import { supabase } from './supabase';

/**
 * Slugs reservados que podem conflitar com rotas ou termos do sistema.
 */
export const RESERVED_SLUGS = [
  'admin', 'dashboard', 'settings', 'profile', 'login', 'register',
  'auth', 'api', 'app', 'help', 'support', 'about', 'terms', 'privacy',
  'new', 'edit', 'delete', 'create', 'form', 'forms', 'share',
  'embed', 'widget', 'public', 'private', 'test', 'demo',
];

/**
 * Converte um titulo em slug URL-friendly.
 * Ex: "Pesquisa de Satisfação" → "pesquisa-de-satisfacao"
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

/**
 * Valida formato e regras de negocio do slug.
 * Retorna mensagem de erro ou null se valido.
 */
export function validateSlug(slug: string): string | null {
  if (slug.length < 3) return 'Mínimo 3 caracteres';
  if (slug.length > 60) return 'Máximo 60 caracteres';
  if (/--/.test(slug)) return 'Hífens consecutivos não permitidos';
  if (/^-|-$/.test(slug)) return 'Não pode iniciar ou terminar com hífen';
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug)) return 'Apenas letras minúsculas, números e hífens';
  if (RESERVED_SLUGS.includes(slug)) return 'Este slug é reservado pelo sistema';
  return null;
}

/**
 * Verifica disponibilidade do slug para o usuario atual.
 * Ignora o proprio formulario (para edicao).
 */
export async function checkSlugAvailability(
  userId: string,
  slug: string,
  excludeFormId?: string
): Promise<{ available: boolean; error: string | null }> {
  try {
    let query = supabase
      .from('forms')
      .select('id')
      .eq('user_id', userId)
      .ilike('slug', slug);

    if (excludeFormId) {
      query = query.neq('id', excludeFormId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) return { available: false, error: error.message };
    return { available: data === null, error: null };
  } catch {
    return { available: false, error: 'Erro ao verificar slug' };
  }
}

/**
 * Busca formulario publicado por username + slug.
 * Usado pela rota publica /@:username/:slug
 */
export async function fetchFormBySlug(
  username: string,
  slug: string
): Promise<{ form: { id: string } | null; error: string | null }> {
  try {
    const { data: userId, error: profileError } = await supabase
      .rpc('get_profile_id_by_username', { lookup_username: username.toLowerCase() });

    if (profileError || !userId) {
      return { form: null, error: 'Usuário não encontrado' };
    }

    const { data: form, error: formError } = await supabase
      .from('forms')
      .select('id')
      .eq('user_id', userId)
      .ilike('slug', slug)
      .eq('is_published', true)
      .single();

    if (formError || !form) {
      return { form: null, error: 'Formulário não encontrado' };
    }

    return { form, error: null };
  } catch {
    return { form: null, error: 'Erro ao carregar formulário' };
  }
}
